import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { del, get, post, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import { toast } from "../../Utils/Helper/toast";

export interface AdminTip {
  id: string;
  url_slug: string;
  title: string;
  subtitle?: string | null;
  category: string;
  audience: string;
  tip_type: string;
  body?: string | null;
  body_markdown?: string | null;
  email_short?: string | null;
  behavioral_trigger?: string | null;
  role_lens?: string | null;
  related_tips?: string[];
  xfactor_levels?: string[];
  seniority?: string[];
  related_categories?: string[];
  paired_pitfall?: string | null;
  video_candidate?: boolean;
  video_url?: string | null;
  link?: string | null;
  link_label?: string | null;
  eb_feature_preview?: string | null;
  source_index?: number;
  active?: boolean;
  published_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export type CreateAdminTipPayload = Omit<
  AdminTip,
  "id" | "created_at" | "updated_at"
> & {
  id?: string;
};

export type UpdateAdminTipPayload = Partial<CreateAdminTipPayload> & {
  tip_id: string;
};

interface AdminTipsState {
  tips: AdminTip[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  createTipSuccess: boolean;
  createTipLoading: boolean;
  updateTipSuccess: boolean;
  updateTipLoading: boolean;
  deleteTipSuccess: boolean;
  deleteTipLoading: boolean;
}

const initialState: AdminTipsState = {
  tips: [],
  loading: false,
  error: null,
  totalCount: 0,
  createTipSuccess: false,
  createTipLoading: false,
  updateTipSuccess: false,
  updateTipLoading: false,
  deleteTipSuccess: false,
  deleteTipLoading: false,
};

export const fetchAdminTips = createAsyncThunk(
  "adminTips/fetchAdminTips",
  async (
    params: {
      audience?: string;
      category?: string;
      tip_type?: string;
      is_active?: boolean;
      search?: string;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await get(API_URLS.fetchAdminTips(params));
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.detail ||
          error.response?.data?.message ||
          "Failed to fetch tips"
      );
    }
  }
);

export const createAdminTip = createAsyncThunk(
  "adminTips/createAdminTip",
  async (payload: CreateAdminTipPayload, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createAdminTip(), payload);
      toast.success("Tip created successfully");
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to create tip";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdminTip = createAsyncThunk(
  "adminTips/updateAdminTip",
  async (payload: UpdateAdminTipPayload, { rejectWithValue }) => {
    try {
      const { tip_id, ...updateData } = payload;
      const response = await put(API_URLS.updateAdminTip(tip_id), updateData);
      toast.success("Tip updated successfully");
      return response;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to update tip";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateAdminTipActiveStatus = createAsyncThunk(
  "adminTips/updateAdminTipActiveStatus",
  async (
    { tip_id, active }: { tip_id: string; active: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(API_URLS.updateAdminTip(tip_id), { active });
      toast.success(active ? "Tip activated" : "Tip deactivated");
      return { tip_id, active, response };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to update tip status";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAdminTip = createAsyncThunk(
  "adminTips/deleteAdminTip",
  async (tipId: string, { rejectWithValue }) => {
    try {
      await del(API_URLS.deleteAdminTip(tipId));
      // Success toast owned by AdminTipsContainer (SuccessNotification)
      return tipId;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to delete tip";
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

const adminTipsSlice = createSlice({
  name: "adminTips",
  initialState,
  reducers: {
    clearCreateAdminTipState: state => {
      state.createTipSuccess = false;
      state.createTipLoading = false;
    },
    clearUpdateAdminTipState: state => {
      state.updateTipSuccess = false;
      state.updateTipLoading = false;
    },
    clearDeleteAdminTipState: state => {
      state.deleteTipSuccess = false;
      state.deleteTipLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAdminTips.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminTips.fulfilled, (state, action) => {
        state.loading = false;
        state.tips = action.payload.items || [];
        state.totalCount = action.payload.total || 0;
      })
      .addCase(fetchAdminTips.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createAdminTip.pending, state => {
        state.createTipLoading = true;
        state.createTipSuccess = false;
      })
      .addCase(createAdminTip.fulfilled, state => {
        state.createTipLoading = false;
        state.createTipSuccess = true;
      })
      .addCase(createAdminTip.rejected, state => {
        state.createTipLoading = false;
      })
      .addCase(updateAdminTip.pending, state => {
        state.updateTipLoading = true;
        state.updateTipSuccess = false;
      })
      .addCase(updateAdminTip.fulfilled, state => {
        state.updateTipLoading = false;
        state.updateTipSuccess = true;
      })
      .addCase(updateAdminTip.rejected, state => {
        state.updateTipLoading = false;
      })
      .addCase(updateAdminTipActiveStatus.pending, state => {
        // Keep tip list interactive; status toggle does not use form loading flags.
        state.error = null;
      })
      .addCase(updateAdminTipActiveStatus.fulfilled, (state, action) => {
        const { tip_id, active } = action.payload;
        const index = state.tips.findIndex(t => t.id === tip_id);
        if (index !== -1) {
          state.tips[index] = { ...state.tips[index], active };
        }
      })
      .addCase(updateAdminTipActiveStatus.rejected, (state, action) => {
        state.error = (action.payload as string) || null;
      })
      .addCase(deleteAdminTip.pending, state => {
        state.deleteTipLoading = true;
        state.deleteTipSuccess = false;
      })
      .addCase(deleteAdminTip.fulfilled, (state, action) => {
        state.deleteTipLoading = false;
        state.deleteTipSuccess = true;
        state.tips = state.tips.filter(t => t.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
      })
      .addCase(deleteAdminTip.rejected, state => {
        state.deleteTipLoading = false;
      });
  },
});

export const {
  clearCreateAdminTipState,
  clearUpdateAdminTipState,
  clearDeleteAdminTipState,
} = adminTipsSlice.actions;

export default adminTipsSlice.reducer;
